/* eslint-disable import/no-cycle */
// eslint-disable-next-line import/no-cycle
import { InputGroupField, Property } from '@/types';
// eslint-disable-next-line import/no-cycle
import { store } from '@/store';
// eslint-disable-next-line import/no-cycle
import {
  removeUnusedTranslations,
  TemplateEditorState as state,
  updateFormContactProperty,
  updateFormFieldConfig,
  hasEmailWidgetInFunnel,
  hasPhoneWidgetInFunnel,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { setAttribute } from '@/components/template-builder/setters';
// eslint-disable-next-line import/no-cycle
import { i18n } from '@/i18n';
import { createInputCheckboxRadioOption, createSelectOption, getTemplateIframeDocument } from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import { generateTranslations, sanitizeTranslationMarkup, setTranslationInState } from '../utils/translate';
// eslint-disable-next-line import/no-cycle

export const setFields = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const fields: HTMLElement[] = Array.from(template?.querySelectorAll(selector));
  fields.forEach((field: HTMLElement) => {
    const id: string = field.querySelector('.spm_form_input')?.getAttribute('id') as string;
    if (id !== 'spm_form_registration_password') {
      if (property.value.includes(id)) field.classList.remove('spm_hidden');
      else field.classList.add('spm_hidden');
    } else if (id === 'spm_form_registration_password' && !field.classList.contains('spm_hidden')) {
      field.classList.add('spm_hidden');
    }
  });
};

const setLabel = (label: HTMLElement, value: string, required: boolean) => {
  // eslint-disable-next-line no-param-reassign
  label.innerHTML = '';
  const span = document.createElement('span');
  span.innerHTML = `${value} :`;
  label.appendChild(span);
  if (required) {
    const asterisk = document.createElement('i');
    asterisk.classList.add('required_asterisk');
    asterisk.innerHTML = '*';
    label.appendChild(asterisk);
  }
};

export const setFieldLabels = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const fields: HTMLElement[] = Array.from(template?.querySelectorAll(selector));
  property.value.forEach((update: InputGroupField) => {
    const fieldToUpdate = fields.filter((field: HTMLElement) => field.querySelector('.spm_form_input')?.getAttribute('id') === update.key)[0];
    const isFieldRequired: boolean = (fieldToUpdate.classList.contains('spm_form_field_base') || ('required' in update && update.required)) ?? false;
    // Change label
    const label = fieldToUpdate.querySelector('label') as HTMLElement;
    setLabel(label, update.value, isFieldRequired);
    // Change placeholder
    const input: HTMLElement = fieldToUpdate.querySelector('.spm_form_input') as HTMLElement;
    if (input) {
      input.setAttribute('placeholder', `${update.value}${isFieldRequired ? '*' : ''}`);

      if (isFieldRequired) {
        input.classList.add('spm_form_required');
      } else {
        input.classList.remove('spm_form_required');
      }
    }
  });
};

export const sortFields = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const fields: HTMLElement[] = Array.from(template?.querySelectorAll(selector));
  const form: HTMLElement = template?.querySelectorAll(selector)[0]?.parentNode as HTMLElement;
  const sortedIds = property.value.map((element: InputGroupField) => element.key);

  fields.sort((a: Element, b: Element) => { // Sort fields in updated order
    const firstIndex = a.querySelector('.spm_form_input')?.getAttribute('id');
    const secondIndex = b.querySelector('.spm_form_input')?.getAttribute('id');
    if (firstIndex && secondIndex) {
      if (sortedIds.indexOf(firstIndex) === -1 || sortedIds.indexOf(secondIndex) === -1) return 1;
      return sortedIds.indexOf(firstIndex) - sortedIds.indexOf(secondIndex);
    }
    return 1;
  })
  // We have to insert fields using "prepend" because form has other elements which have to stay at the end
  // so we have to reverse array before prepend fields
    .reverse()
    .forEach((field: Element) => {
      if (form) form.prepend(field);
    });
};

export const setMessage = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const container: HTMLElement = template?.querySelector(selector) as HTMLElement;
  const element = container.querySelector('.spm_step2');
  if (element) {
    element.innerHTML = property.value;
  }
};

export const setContactProperty = (selector: string, property: Property) => {
  if (state.template) {
    const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
    if (elementId && elementId.length) {
      updateFormContactProperty(elementId[1], property.value || '');
      const template = getTemplateIframeDocument();
      const widget = template?.querySelector(`#${elementId[1]}`);
      if (widget) {
        widget.setAttribute('data-contactproperty', property.value);
      }
    }
  }
};

/**
 * Map from sorted channel comma-key to i18n translation sub-key.
 * The actual translated messages live in templateBuilder.fields.disclosureDefaultMessages.*
 */
const CHANNEL_KEY_TO_I18N: Record<string, string> = {
  email: 'email',
  sms: 'sms',
  whatsapp: 'whatsapp',
  'email,sms': 'email_sms',
  'email,whatsapp': 'email_whatsapp',
  'sms,whatsapp': 'sms_whatsapp',
  'email,sms,whatsapp': 'email_sms_whatsapp',
};

/** Normalize text for comparison: strip HTML tags, collapse whitespace, lowercase */
const normalizeForComparison = (html: string): string => html
  .replace(/<[^>]*>/g, '')
  .replace(/&amp;/g, '&')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

/** All known default messages (EN + FR) normalized, for checking if current content is still a default */
const ALL_DEFAULT_TEXTS = new Set([
  // EN defaults
  'by submitting this form, you agree to receive marketing emails from {var=shop.name}. you can unsubscribe at any time via the link in our emails. see our privacy policy.',
  'by providing your phone number, you consent to receive recurring marketing text messages from {var=shop.name}. message frequency varies. msg & data rates may apply. reply stop to unsubscribe. see our privacy policy.',
  'by providing your phone number, you consent to receive marketing messages from {var=shop.name} via whatsapp. you can opt out at any time by replying stop. see our privacy policy.',
  'by submitting this form, you agree to receive marketing emails and text messages from {var=shop.name}. message frequency varies. msg & data rates may apply. reply stop to unsubscribe from sms. you can unsubscribe from emails via the link in our messages. see our privacy policy.',
  'by submitting this form, you agree to receive marketing emails and whatsapp messages from {var=shop.name}. you can unsubscribe from emails via the link in our messages or reply stop on whatsapp. see our privacy policy.',
  'by providing your phone number, you consent to receive marketing text messages and whatsapp messages from {var=shop.name}. message frequency varies. msg & data rates may apply. reply stop to unsubscribe. see our privacy policy.',
  'by submitting this form, you agree to receive marketing emails, text messages and whatsapp messages from {var=shop.name}. message frequency varies. msg & data rates may apply. reply stop to unsubscribe from sms/whatsapp. you can unsubscribe from emails via the link in our messages. see our privacy policy.',
  // FR defaults
  'en soumettant ce formulaire, vous acceptez de recevoir des emails marketing de la part de {var=shop.name}. vous pouvez vous désabonner à tout moment via le lien dans nos emails. consultez notre politique de confidentialité.',
  'en fournissant votre numéro de téléphone, vous consentez à recevoir des sms marketing de la part de {var=shop.name}. la fréquence des messages varie. des frais de messagerie et de données peuvent s\'appliquer. répondez stop pour vous désabonner. consultez notre politique de confidentialité.',
  'en fournissant votre numéro de téléphone, vous consentez à recevoir des messages marketing de la part de {var=shop.name} via whatsapp. vous pouvez vous désinscrire à tout moment en répondant stop. consultez notre politique de confidentialité.',
  'en soumettant ce formulaire, vous acceptez de recevoir des emails et sms marketing de la part de {var=shop.name}. la fréquence des messages varie. des frais de messagerie et de données peuvent s\'appliquer. répondez stop pour vous désabonner des sms. vous pouvez vous désabonner des emails via le lien dans nos messages. consultez notre politique de confidentialité.',
  'en soumettant ce formulaire, vous acceptez de recevoir des emails et messages whatsapp marketing de la part de {var=shop.name}. vous pouvez vous désabonner des emails via le lien dans nos messages ou répondre stop sur whatsapp. consultez notre politique de confidentialité.',
  'en fournissant votre numéro de téléphone, vous consentez à recevoir des sms et messages whatsapp marketing de la part de {var=shop.name}. la fréquence des messages varie. des frais de messagerie et de données peuvent s\'appliquer. répondez stop pour vous désabonner. consultez notre politique de confidentialité.',
  'en soumettant ce formulaire, vous acceptez de recevoir des emails, sms et messages whatsapp marketing de la part de {var=shop.name}. la fréquence des messages varie. des frais de messagerie et de données peuvent s\'appliquer. répondez stop pour vous désabonner des sms/whatsapp. vous pouvez vous désabonner des emails via le lien dans nos messages. consultez notre politique de confidentialité.',
]);

/** Get the translated default message for a given channel combination */
const getDefaultDisclosureMessage = (channels: string[]): string => {
  const key = [...channels].sort().join(',');
  const i18nKey = CHANNEL_KEY_TO_I18N[key] || 'email';
  return (i18n.global as any).t(`templateBuilder.fields.disclosureDefaultMessages.${i18nKey}`);
};

/** Clés i18n de tous les textes de divulgation par défaut (toutes combinaisons de canaux). */
const DISCLOSURE_DEFAULT_I18N_KEYS = ['email', 'sms', 'whatsapp', 'email_sms', 'email_whatsapp', 'sms_whatsapp', 'email_sms_whatsapp'];

/**
 * Le contenu courant est-il un texte par défaut (non personnalisé) ?
 * On vérifie à la fois la liste historique (ALL_DEFAULT_TEXTS, pour les widgets déjà en place)
 * ET les textes i18n COURANTS (dynamique, FR + EN) — ainsi toute édition de
 * `disclosureDefaultMessages` reste reconnue comme défaut et le texte continue de s'adapter
 * aux canaux (sinon, éditer un texte par défaut le désynchronisait et bloquait l'adaptation).
 */
const isDefaultDisclosureContent = (html: string): boolean => {
  const normalized = normalizeForComparison(html);
  if (ALL_DEFAULT_TEXTS.has(normalized)) {
    return true;
  }
  return ['fr', 'en'].some((locale) => DISCLOSURE_DEFAULT_I18N_KEYS.some((key) => {
    const msg = (i18n.global as any).t(
      `templateBuilder.fields.disclosureDefaultMessages.${key}`,
      {},
      { locale },
    );
    return typeof msg === 'string' && normalizeForComparison(msg) === normalized;
  }));
};

export const setDisclosureChannels = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    // Filter channels: only allow channels whose identifier widget is present
    const hasEmail = hasEmailWidgetInFunnel();
    const hasPhone = hasPhoneWidgetInFunnel();
    const rawChannels: string[] = Array.isArray(property.value)
      ? property.value
      : (property.value || '').split(',').filter(Boolean);
    const validChannels = rawChannels.filter((ch: string) => {
      if (ch === 'email') return hasEmail;
      if (ch === 'sms' || ch === 'whatsapp') return hasPhone;
      return false;
    });

    const value = validChannels.join(',');
    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      widget.setAttribute('data-disclosure-channels', value);

      // Auto-update content if it's still a default message
      const richEditor = widget.querySelector('.spm_rich_editor') as HTMLElement;
      if (richEditor && validChannels.length > 0) {
        const currentContent = sanitizeTranslationMarkup(richEditor).innerHTML;
        if (isDefaultDisclosureContent(currentContent)) {
          const newDefault = getDefaultDisclosureMessage(validChannels);
          sanitizeTranslationMarkup(richEditor).innerHTML = newDefault;
          setTranslationInState(richEditor, 'markup');
        }
      }
    }
    // Persist to state for evaluator and template save
    updateFormFieldConfig(elementId[1], 'data-disclosure-channels', value);

    // Re-run getters to update property values from DOM, then force panel re-mount
    store.dispatch('liveEditor/refreshWidgetFields');
    store.commit('liveEditor/refreshConfigurationPanelKey');
  }
};

/** Get translated consent default label for a channel */
const getConsentDefaultLabel = (channel: string): string => (i18n.global as any).t(`templateBuilder.fields.consentDefaultLabels.${channel}`);

/** Channel to contactProperty mapping */
const CONSENT_CHANNEL_TO_PROPERTY: Record<string, string> = {
  email: 'consent_email',
  sms: 'consent_sms',
  whatsapp: 'consent_whatsapp',
};

/** All known default consent labels (FR + EN) for detection */
const ALL_DEFAULT_CONSENT_LABELS = new Set([
  "j'accepte de recevoir les emails marketing de {var=shop.name}",
  "j'accepte de recevoir les sms marketing de {var=shop.name}",
  "j'accepte de recevoir les messages whatsapp de {var=shop.name}",
  'i agree to receive marketing emails from {var=shop.name}',
  'i agree to receive marketing sms from {var=shop.name}',
  'i agree to receive whatsapp marketing messages from {var=shop.name}',
  // CNIL pixels — libellé combiné email + suivi des ouvertures (mode groupé)
  "j'accepte de recevoir les emails marketing de {var=shop.name} et la mesure de leur ouverture",
  'i agree to receive marketing emails from {var=shop.name} and to email open tracking',
]);

/** Check if the current label is a default consent label */
const isDefaultConsentLabel = (text: string): boolean => ALL_DEFAULT_CONSENT_LABELS.has(text.replace(/<br\s*\/?>/gi, '').replace(/\n/gi, '').toLowerCase().trim());

export const setConsentChannel = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const channel = property.value || '';
    const contactProperty = CONSENT_CHANNEL_TO_PROPERTY[channel] || '';

    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      widget.setAttribute('data-consent-channel', channel);
      widget.setAttribute('data-contactproperty', contactProperty);

      // CNIL pixels — le mode granulaire est email-only. Si on quitte le canal email alors
      // que le widget est en granulaire, on DÉMANTÈLE la structure (maître + « Gérer finement »
      // + panneau) et on revient à une case simple ; sinon, on met juste à jour le libellé.
      const isGranular = widget.getAttribute('data-consent-mode') === 'granular';
      if (isGranular && channel && channel !== 'email') {
        const wrapper = widget.querySelector('.spm_widget_input_checkbox__options-wrapper');
        if (wrapper) {
          const singleLabel = getConsentDefaultLabel(channel);
          const html = `<div class="spm_widget_input_checkbox__options-container">${createInputCheckboxRadioOption(elementId[1], 'checkbox', singleLabel, '', '1')}</div>`;
          const { newHtml } = generateTranslations(html, {
            newElement: true,
            translateVariables: true,
            forceTranslateHiddenElement: true,
          });
          wrapper.innerHTML = newHtml;
        }
        widget.removeAttribute('data-consent-mode');
        updateFormFieldConfig(elementId[1], 'consentMode', 'grouped');
      } else {
        // Update label if it's still a default label
        const labelElement = widget.querySelector('.spm_widget_input_checkbox__options label') as HTMLElement;
        if (labelElement && channel) {
          const currentText = sanitizeTranslationMarkup(labelElement).innerHTML
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/\n/gi, '')
            .trim();
          if (!currentText || isDefaultConsentLabel(currentText)) {
            const newLabel = getConsentDefaultLabel(channel);
            sanitizeTranslationMarkup(labelElement).innerHTML = newLabel;
            setTranslationInState(labelElement, 'markup');
          }
        }
      }
    }

    // Persist to state
    updateFormFieldConfig(elementId[1], 'data-consent-channel', channel);
    updateFormContactProperty(elementId[1], contactProperty);

    // Refresh widget fields for form actions evaluator
    store.dispatch('liveEditor/refreshWidgetFields');
    // Rafraîchit le panneau de config pour que l'input « Libellé » reflète le nouveau
    // libellé par défaut (son getter relit le DOM), et que la visibilité du toggle suivi
    // suive le canal sélectionné.
    store.commit('liveEditor/refreshConfigurationPanelKey');
  }
};

/**
 * CNIL pixels — mode GROUPÉ : la case de consentement (email) couvre aussi le suivi
 * des ouvertures. Persiste `configs.trackingConsent` (lu par forms.js au submit →
 * formData.tracking_view + action 'tracking_consent') + l'attribut DOM data-tracking-consent
 * (lu par le getter de l'éditeur).
 */
export const setTrackingConsentGrouped = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const enabled = property.value === true || property.value === 'true';
    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      widget.setAttribute('data-tracking-consent', enabled ? 'true' : 'false');

      // Met à jour le libellé (canal email uniquement) s'il est encore un libellé par
      // défaut : ON → libellé combiné (email + suivi) ; OFF → libellé email seul.
      const channel = widget.getAttribute('data-consent-channel');
      const labelElement = widget.querySelector('.spm_widget_input_checkbox__options label') as HTMLElement;
      if (labelElement && channel === 'email') {
        const currentText = sanitizeTranslationMarkup(labelElement).innerHTML
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/\n/gi, '')
          .trim();
        if (!currentText || isDefaultConsentLabel(currentText)) {
          const newLabel = enabled
            ? getConsentDefaultLabel('email_tracking')
            : getConsentDefaultLabel('email');
          sanitizeTranslationMarkup(labelElement).innerHTML = newLabel;
          setTranslationInState(labelElement, 'markup');
          store.commit('liveEditor/refreshConfigurationPanelKey');
        }
      }
    }
    updateFormFieldConfig(elementId[1], 'trackingConsent', enabled);
    updateFormFieldConfig(elementId[1], 'data-tracking-consent', enabled ? 'true' : 'false');
  }
};

/**
 * Case de consentement obligatoire. L'attribut data-consent-required est posé sur la racine
 * du widget (lu par forms.js au submit) : il survit à la reconstruction des cases quand on
 * change de canal ou de mode. Persiste aussi configs.consentRequired (lu par le getter).
 */
export const setConsentRequired = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const required = property.value === true || property.value === 'true';
    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      if (required) {
        widget.setAttribute('data-consent-required', 'true');
      } else {
        widget.removeAttribute('data-consent-required');
      }
    }
    updateFormFieldConfig(elementId[1], 'consentRequired', required);
  }
};

/**
 * CNIL pixels — bascule le widget consent entre mode GROUPE (case unique) et GRANULAIRE
 * (« Gerer finement » : case maitre + sous-cases par finalite). Reconstruit le contenu de
 * .spm_widget_input_checkbox__options-wrapper. Finalites v1 = email + ouvertures (le clic
 * conditionne par shopDataSubscriptionPage_showTrackingClic = suite documentee). Cote
 * contact : tout decoche (RGPD). data-consent-mode lu par forms.js (serialisation + interactivite).
 */
export const setConsentMode = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (!elementId || !elementId.length) return;
  const wid = elementId[1];
  const widget = template?.querySelector(`#${wid}`);
  if (!widget) return;
  const wrapper = widget.querySelector('.spm_widget_input_checkbox__options-wrapper');
  if (!wrapper) return;

  const isGranular = property.value === true || property.value === 'true';
  const tr = (key: string): string => (i18n.global as any).t(key);

  let html = '';
  if (isGranular) {
    const purposes = [
      { purpose: 'consent_email', label: tr('templateBuilder.fields.consentGranular.purposes.consent_email') },
      { purpose: 'tracking_view', label: tr('templateBuilder.fields.consentGranular.purposes.tracking_view') },
    ];
    let childrenHtml = '';
    purposes.forEach((p, i) => {
      childrenHtml += `<div class="spm_form_field spm_form_checkbox spm_widget_input_checkbox__options spm_consent_granular__child">`
        + `<input type="checkbox" data-consent-purpose="${p.purpose}" id="spm_form_input_checkbox--${wid}-p${i}" value="1" class="spm_widget_input_radio__input" />`
        + `<label for="spm_form_input_checkbox--${wid}-p${i}" class="spm_force_inline">${p.label}</label></div>`;
    });
    html = `<div class="spm_widget_input_checkbox__options-container">`
      + `<div class="spm_form_field spm_form_checkbox spm_widget_input_checkbox__options spm_consent_granular__master">`
      + `<input type="checkbox" data-consent-master="1" id="spm_form_input_checkbox--${wid}-master" value="1" class="spm_widget_input_radio__input" />`
      + `<label for="spm_form_input_checkbox--${wid}-master" class="spm_force_inline">${tr('templateBuilder.fields.consentDefaultLabels.email_tracking')}</label></div></div>`
      + `<button type="button" class="spm_consent_granular__toggle">${tr('templateBuilder.fields.consentGranular.manage')}</button>`
      + `<div class="spm_consent_granular__panel">${childrenHtml}</div>`;
  } else {
    const channel = widget.getAttribute('data-consent-channel') || 'email';
    const labelKey = channel === 'email' ? 'email_tracking' : channel;
    const singleLabel = tr(`templateBuilder.fields.consentDefaultLabels.${labelKey}`);
    html = `<div class="spm_widget_input_checkbox__options-container">${createInputCheckboxRadioOption(wid, 'checkbox', singleLabel, '', '1')}</div>`;
  }

  const { newHtml } = generateTranslations(html, {
    newElement: true,
    translateVariables: true,
    forceTranslateHiddenElement: true,
  });
  wrapper.innerHTML = newHtml;

  if (isGranular) {
    widget.setAttribute('data-consent-mode', 'granular');
    updateFormFieldConfig(wid, 'consentMode', 'granular');
    updateFormFieldConfig(wid, 'data-consent-mode', 'granular');
  } else {
    widget.removeAttribute('data-consent-mode');
    updateFormFieldConfig(wid, 'consentMode', 'grouped');
  }
  removeUnusedTranslations();
  store.commit('liveEditor/refreshConfigurationPanelKey');
};

const REGISTER_NEWSLETTER_DEFAULT_LABELS = new Set([
  "je souhaite m'inscrire à la newsletter",
  'i would like to subscribe to the newsletter',
  "je souhaite m'inscrire à la newsletter et autoriser la mesure de l'ouverture de mes emails pour des contenus adaptés",
  'i would like to subscribe to the newsletter and allow open tracking of my emails for relevant content',
]);

/**
 * CNIL pixels — toggle « suivi des ouvertures » du widget Création de compte (V3). Pose la classe
 * spm_form_tracking_consent sur .spm_form (lue par formsV3.js + back validWidgetForm) ET adapte le
 * LIBELLÉ de la case newsletter (combiné si activé, simple sinon) — uniquement si le libellé est
 * encore un défaut (préserve une personnalisation). Le suivi est groupé avec l'opt-in newsletter,
 * donc pas de pavé de mention séparé (le libellé de la case porte l'information).
 */
export const setTrackingConsentCreateAccount = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (!elementId || !elementId.length) return;
  const wid = elementId[1];
  const widget = template?.querySelector(`#${wid}`);
  if (!widget) return;
  const enabled = property.value === true || property.value === 'true';
  const tr = (key: string): string => (i18n.global as any).t(key);

  // 1. Classe sur .spm_form (lue par formsV3.js + back validWidgetForm)
  const form = widget.querySelector('.spm_form');
  if (form) {
    if (enabled) form.classList.add('spm_form_tracking_consent');
    else form.classList.remove('spm_form_tracking_consent');
  }

  // 2. Adapte le libellé de la case newsletter, seulement s'il est encore un défaut
  const label = widget.querySelector('label[for="spm_form_registration_newsletter"]') as HTMLElement | null;
  if (label) {
    const current = sanitizeTranslationMarkup(label).innerHTML
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/\n/gi, '')
      .trim()
      .toLowerCase();
    if (!current || REGISTER_NEWSLETTER_DEFAULT_LABELS.has(current)) {
      const newLabel = enabled
        ? tr('templateBuilder.fields.registerNewsletterWithTracking')
        : tr('templateBuilder.fields.registerNewsletter');
      sanitizeTranslationMarkup(label).innerHTML = newLabel;
      setTranslationInState(label, 'markup');
    }
  }

  // 3. Persiste l'état du toggle + rafraîchit le panneau (refreshWidgetFields relit les getters,
  // notamment le champ « Texte d'inscription à la newsletter » qui doit refléter le nouveau libellé).
  updateFormFieldConfig(wid, 'trackingConsent', enabled);
  removeUnusedTranslations();
  store.dispatch('liveEditor/refreshWidgetFields');
  store.commit('liveEditor/refreshConfigurationPanelKey');
};

export const setDateFormat = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const widget = template.querySelector(`#${elementId[1]}`);
    if (widget) {
      widget.setAttribute(property.name, property.value);
    }
  }
};

export const setCheckboxOptionsProperty = (selector: string, property: Property) => {
  if (state.template) {
    const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
    if (elementId && elementId.length) {
      const template = getTemplateIframeDocument();
      const isCheckbox = property.type === 'checkbox';

      // Delete old options
      const existingOptionElements = Array.from(template.querySelectorAll(selector));
      const existingOptionIds: string[] = [];
      existingOptionElements.forEach((existingOptionElement) => {
        const existingInputElement = existingOptionElement.querySelector('input');
        if (existingInputElement) {
          existingOptionIds.push(existingInputElement.id);
        }
      });
      const actualOptionIds = property.value.map((option: any) => option.optionId);
      const optionsToDelete = existingOptionIds.filter((existingOptionId) => !actualOptionIds.includes(existingOptionId));
      optionsToDelete.forEach((optionToDelete) => {
        const optionToDeleteElement = template.querySelector(`input[id="${optionToDelete}"]`);
        if (optionToDeleteElement && optionToDeleteElement.parentElement) {
          optionToDeleteElement.parentElement.remove();
        }
      });
      removeUnusedTranslations();

      // Update translation, input value attribute
      if (Array.isArray(property.value)) {
        property.value.forEach((propertyValue) => {
          const labelElement = template.querySelector(`label[for="${propertyValue.optionId}"]`);
          if (labelElement) {
            sanitizeTranslationMarkup(labelElement as HTMLElement).innerHTML = propertyValue.optionLabel;
            setTranslationInState(labelElement as HTMLElement, 'markup');
            const inputElement = template.querySelector(`input[id="${propertyValue.optionId}"]`);
            if (inputElement) {
              inputElement.setAttribute('value', propertyValue.optionValue);
            }
          } else {
            const optionIdSuffix = propertyValue.optionId.match(/-(\d+)$/);
            const html = createInputCheckboxRadioOption(
              elementId[1],
              property.type || 'checkbox',
              propertyValue.optionLabel,
              propertyValue.optionValue,
              optionIdSuffix ? optionIdSuffix[1] : 1,
              isCheckbox,
            );
            const { newHtml } = generateTranslations(
              html,
              {
                newElement: true,
                translateVariables: true,
                forceTranslateHiddenElement: true,
              },
            );
            const newOptionHtmlElement = document.createElement('div');
            newOptionHtmlElement.innerHTML = newHtml;

            const widgetScope = template.querySelector(`#${elementId[1]}`);
            const options = widgetScope?.querySelectorAll(`.spm_widget_input_${property.type || ''}__options`);
            const lastOption = options?.[options.length - 1];
            if (lastOption) {
              lastOption.after(newOptionHtmlElement.firstChild as HTMLElement);
            } else {
              const container = widgetScope?.querySelector(`.spm_widget_input_${property.type || ''}__options-container`);
              const containerLabel = container?.querySelector('label');
              if (containerLabel) {
                containerLabel.after(newOptionHtmlElement.firstChild as HTMLElement);
              } else if (container) {
                container.appendChild(newOptionHtmlElement.firstChild as HTMLElement);
              }
            }
          }
        });
      }

      // Sort options
      const widgetElement = template.querySelector(`#${elementId[1]}`);
      const container = widgetElement?.querySelector(`.spm_widget_input_${property.type || ''}__options-container`);
      if (Array.isArray(property.value)) {
        for (let i = property.value.length - 1; i >= 0; i--) {
          const lastOptionElement = template.querySelector(`input[id="${property.value[i].optionId}"]`)?.parentElement;
          const nextOptionElement = i - 1 >= 0 ? template.querySelector(`input[id="${property.value[i - 1].optionId}"]`)?.parentElement : null;
          if (lastOptionElement && nextOptionElement && container) {
            container.insertBefore(nextOptionElement, lastOptionElement);
          }
        }
      }

      updateFormFieldConfig(elementId[1], property.name, property.value);
    }
  }
};

export const setSelectOptionsProperty = (selector: string, property: Property) => {
  if (state.template) {
    const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
    if (elementId && elementId.length) {
      const template = getTemplateIframeDocument();
      const selectElement = template.querySelector(selector);
      const isCustomSelect = selectElement?.classList.contains('spm_custom_select');

      let currentValue;
      if (selectElement) {
        if (isCustomSelect) {
          const hiddenInput = selectElement.querySelector('.spm_custom_select__hidden_input') as HTMLInputElement;
          currentValue = hiddenInput?.value;
        } else {
          currentValue = (selectElement as HTMLSelectElement).value;
        }
      }

      // Delete old options
      if (selectElement) {
        const optionSelector = isCustomSelect ? '.spm_custom_select__option' : 'option:not(.spm_widget_input_select__placeholder)';
        const existingOptionElements = Array.from(selectElement.querySelectorAll(optionSelector));
        const existingOptionIds: string[] = [];
        existingOptionElements.forEach((existingOptionElement) => {
          existingOptionIds.push(existingOptionElement.id);
        });
        const actualOptionIds = property.value.map((option: any) => option.optionId);
        const optionsToDelete = existingOptionIds.filter((existingOptionId) => !actualOptionIds.includes(existingOptionId));
        optionsToDelete.forEach((optionToDelete) => {
          const optionToDeleteElement = template.querySelector(`${isCustomSelect ? '.spm_custom_select__option' : 'option'}[id="${optionToDelete}"]`);
          if (optionToDeleteElement) {
            optionToDeleteElement.remove();
          }
        });
        removeUnusedTranslations();
      }

      // Update translation, input value attribute
      if (selectElement && Array.isArray(property.value)) {
        const optionsContainer = isCustomSelect ? selectElement.querySelector('.spm_custom_select__options') : selectElement;
        const optionElementSelector = isCustomSelect ? '.spm_custom_select__option' : 'option';

        property.value.forEach((propertyValue) => {
          const optionElement = selectElement.querySelector(`${optionElementSelector}[id="${propertyValue.optionId}"]`);
          if (optionElement) {
            setAttribute(
              `${optionElementSelector}[id="${propertyValue.optionId}"]`,
              {
                name: 'data-selectoptionlabel',
                value: propertyValue.optionLabel,
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                getter: () => {},
                setters: [],
              },
            );
            sanitizeTranslationMarkup(optionElement as HTMLElement).innerHTML = propertyValue.optionLabel;
            if (isCustomSelect) {
              optionElement.setAttribute('data-value', propertyValue.optionValue);
            } else {
              optionElement.setAttribute('value', propertyValue.optionValue);
            }
          } else {
            const optionIdSuffix = propertyValue.optionId.match(/-(\d+)$/);
            const html = createSelectOption(elementId[1], propertyValue.optionLabel, propertyValue.optionValue, optionIdSuffix ? optionIdSuffix[1] : 1);
            const { newHtml } = generateTranslations(
              html,
              {
                newElement: true,
                translateVariables: true,
                forceTranslateHiddenElement: true,
              },
            );
            const newOptionHtmlElement = document.createElement('div');
            newOptionHtmlElement.innerHTML = newHtml;

            if (optionsContainer) {
              optionsContainer.append(newOptionHtmlElement.firstChild as HTMLElement);
            }
          }
        });

        // Sort options
        property.value.forEach((propertyValue) => {
          const optionElement = selectElement.querySelector(`${optionElementSelector}[id="${propertyValue.optionId}"]`);
          if (optionElement && optionsContainer) {
            optionsContainer.appendChild(optionElement);
          }
        });

        if (currentValue) {
          if (isCustomSelect) {
            const hiddenInput = selectElement.querySelector('.spm_custom_select__hidden_input') as HTMLInputElement;
            if (hiddenInput) {
              hiddenInput.value = currentValue;
              // Update displayed value
              const selectedOption = selectElement.querySelector(`${optionElementSelector}[data-value="${currentValue}"]`);
              const valueElement = selectElement.querySelector('.spm_custom_select__value');
              if (selectedOption && valueElement) {
                valueElement.textContent = selectedOption.textContent;
                valueElement.classList.remove('spm_custom_select__placeholder');
              }
            }
          } else {
            (selectElement as HTMLSelectElement).value = currentValue;
          }
        }
      }

      updateFormFieldConfig(elementId[1], property.name, property.value);
    }
  }
};

/**
 * Generic setter to save form field config in state
 * Used for fields with fieldId that need to be persisted in configuration.forms[widgetId].configs
 */
export const saveFormFieldConfig = (selector: string, property: Property, fieldId: string) => {
  if (state.template) {
    const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
    if (elementId && elementId.length) {
      updateFormFieldConfig(elementId[1], fieldId, property.value);
    }
  }
};
